import './util/embeddedBundleSession'
import ReactDOM from 'react-dom/client'
import { Provider } from 'react-redux'
import App from './App'
import store, { persistor } from './store'
import { PersistGate } from 'redux-persist/integration/react'
import WebSocketProvider from './components/webSocketConnection/WebSocket'
import ProviderWebSocketProvider from './components/webSocketConnection/ProviderWebSocket'
import { ErrorBoundary } from 'react-error-boundary'
import ErrorFallback from './ErrorFallback'
import ThemeProvider from './theme/ThemeProvider'
import { BrowserRouter } from 'react-router-dom'
import * as serviceWorkerRegistration from './serviceWorkerRegistration'
import ROUTES from './assets/routes'
import ShortcutsProvider from './components/KeyboardShortcuts/Shortcuts.context'
import EmbeddedWrapper from './embeddedWrapper'
import { isEmbeddedModeSet } from './util/general'
import SecurityManager from './util/securityManager'
import { UserProvider } from './context/UserContext'
import { isAuthenticated } from './util/authentication'
import { isAuthRoute } from './util/initialLoadGate'
import { prefetchUserProfile } from './services/apis/user'

const embeddedMode = isEmbeddedModeSet();
const routerBaseName = import.meta.env.BASE_URL === '/' ? undefined : import.meta.env.BASE_URL.replace(/\/$/, '')

const root = ReactDOM.createRoot(document.getElementById("root")!)

window.securityManager = new SecurityManager();

// Warm start: the chart page cannot request candles until the profile response has opened the
// initial-load gate and the securities master is in memory. Both are started here, before React
// renders and redux-persist rehydrates, and App joins the same promises when it mounts.
if (!embeddedMode && isAuthenticated() && !isAuthRoute()) {
    window.securityManager.loadSecurities().catch(() => {
        // Reported by App, which awaits this same promise.
    })
    prefetchUserProfile()
}

// Disable console logs in production only when deployed (not on localhost) - allows debugging locally
const isProductionDeployed = import.meta.env.MODE === "production" && !window.location.origin.includes("localhost");
const debugLogsEnabled = import.meta.env.VITE_DEBUG_LOGS === "true";
if (isProductionDeployed && !debugLogsEnabled) {
    console.log = () => {};
    console.debug = () => {};
    console.warn = () => {};
}

const app = (
    <WebSocketProvider>
        <ProviderWebSocketProvider>
            <ShortcutsProvider>
                <App key={'tick2trade'} />
            </ShortcutsProvider>
        </ProviderWebSocketProvider>
    </WebSocketProvider>
)

root.render(
    <Provider store={store}>
        <ThemeProvider>
            <UserProvider>
                <BrowserRouter basename={routerBaseName} future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                    <EmbeddedWrapper>
                        <ErrorBoundary
                            key={'app-fallback'}
                            FallbackComponent={ErrorFallback}
                            onReset={(reset) => {
                                //@ts-ignore
                                if (reset?.args?.[0]) {
                                    window.location.href = import.meta.env.BASE_URL
                                } else {
                                    window.location.reload()
                                }
                            }}
                        >
                            {embeddedMode ? app : (
                                <PersistGate loading={null} persistor={persistor}>
                                    {app}
                                </PersistGate>
                            )}
                        </ErrorBoundary>
                    </EmbeddedWrapper>
                </BrowserRouter>
            </UserProvider>
        </ThemeProvider>
    </Provider>
)

serviceWorkerRegistration.disableServiceWorker()